import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

// Confirmed engraving choices, keyed by product id. Module-scoped so they
// survive the section morph that re-renders the product form on every
// variant change (the morph strips dynamically added hidden inputs).
const engravingState = new Map();

if (!customElements.get('product-engraving')) {
  class ProductEngraving extends Component {
    #abortController;

    connectedCallback() {
      super.connectedCallback();
      this.productId = this.closest('variant-picker-component')?.getAttribute('data-product-id');
      this.layerElement = this.closest('layer-component');

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      document.addEventListener(ThemeEvents.variantUpdate, () => this.#restore(), { signal });
      document.addEventListener('submit', this.#guardSubmit, { capture: true, signal });

      this.#initEvent(signal);
      this.#changeType();
      this.#restore();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      super.disconnectedCallback();
    }

    get productForm() {
      return document
        .querySelector('input[name="product-id"][value="' + this.productId + '"]')
        ?.closest('form');
    }

    get #picker() {
      return this.closest('variant-picker-component');
    }

    get #rappels() {
      const scope = this.#picker || document;
      return scope.querySelectorAll('.product-engraving-container--selected');
    }

    #rappelValue(type) {
      const scope = this.#picker || document;
      return scope.querySelector(
        '.product-engraving-container--selected[data-type="' + type + '"] .product-engraving-container--selected-value'
      );
    }

    // Events are delegated so the listeners survive DOM morphs that may
    // replace the inner nodes.
    #initEvent(signal) {
      this.addEventListener(
        'change',
        (e) => {
          if (this.refs.type?.includes(e.target)) {
            e.stopPropagation();
            this.#changeType();
          }
        },
        { signal }
      );

      this.addEventListener(
        'click',
        (e) => {
          if (this.refs.confirm && (e.target === this.refs.confirm || this.refs.confirm.contains(e.target))) {
            this.#confirm(e);
          }
        },
        { signal }
      );
    }

    #confirm(event) {
      event.stopPropagation();
      const selectedType = this.refs.type?.find((type) => type.checked);
      const note = this.refs.note?.querySelector('textarea');
      if (!selectedType || !note) return;

      const value = note.value.trim();
      const maxCharacter = parseInt(selectedType.dataset.maxCharacter, 10);

      if (!Number.isNaN(maxCharacter) && value.length > maxCharacter) {
        this.#showError(this.dataset.errorMax?.replace('[max]', String(maxCharacter)));
        return;
      }
      this.#hideError();

      if (value === '') {
        engravingState.delete(this.productId);
        this.#removeProperties();
        this.#updateRappels(null);
      } else {
        const state = { font: selectedType.value, note: value, token: this.#createToken() };
        engravingState.set(this.productId, state);
        this.#applyProperties(state);
        this.#updateRappels(state);
        this.#selectEngravedVariant();
      }

      this.layerElement?.close();
    }

    // Re-applies the confirmed engraving after the product section is
    // morphed: hidden inputs, font selection, textarea content and rappels
    // are all reset to the server-rendered state by the morph.
    #restore() {
      const state = engravingState.get(this.productId);
      if (!state) return;

      // A variant change means a different cart line: hand it a fresh token so
      // every engraved piece keeps its own fee line, instead of two pieces
      // sharing one and making its quantity ambiguous.
      state.token = this.#createToken();

      const savedType = this.refs.type?.find((type) => type.value === state.font);
      if (savedType) savedType.checked = true;
      this.#changeType();

      const note = this.refs.note?.querySelector('textarea');
      if (note) note.value = state.note;

      this.#applyProperties(state);
      this.#updateRappels(state);
    }

    #applyProperties(state) {
      const form = this.productForm;
      if (!form) return;

      this.#setHiddenInput(form, 'properties[Select Font]', state.font);
      this.#setHiddenInput(form, 'properties[Engraving]', state.note);
      this.#setHiddenInput(form, 'properties[_engraving_token]', state.token);
      this.#applyFee(form);
    }

    #removeProperties() {
      const form = this.productForm;
      if (!form) return;

      form.querySelector('[name="properties[Select Font]"]')?.remove();
      form.querySelector('[name="properties[Engraving]"]')?.remove();
      form.querySelector('[name="properties[_engraving_token]"]')?.remove();
      delete form.dataset.engravingFeeVariant;
    }

    // The flat engraving fee is carried by a dedicated product: declaring its
    // variant on the form tells product-form.js to add it as a second line
    // item, linked to the piece by the shared engraving token.
    #applyFee(form) {
      const feeVariantId = this.dataset.feeVariantId;
      if (!feeVariantId) return;

      form.dataset.engravingFeeVariant = feeVariantId;
    }

    // Links the piece and its fee line item together, and keeps two pieces
    // engraved differently on separate cart lines.
    #createToken() {
      return crypto.randomUUID?.() ?? String(Math.random()).slice(2);
    }

    #setHiddenInput(form, name, value) {
      let input = form.querySelector('[name="' + name + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    }

    #updateRappels(state) {
      const font = this.#rappelValue('font');
      const note = this.#rappelValue('note');
      if (font) font.textContent = state ? state.font : '';
      if (note) note.textContent = state ? state.note : '';

      this.#rappels.forEach((rappel) => {
        rappel.classList.toggle('--show', state !== null);
      });
    }

    // The engraving fee is carried by the last value of the "Type" variant
    // option. Selecting it here guarantees the fee is in the cart whenever
    // an engraving is confirmed.
    #selectEngravedVariant() {
      const engravedInput = this.#picker?.querySelector(
        '.engraving-variant .variant-picker__values label:last-child input'
      );
      if (engravedInput && !engravedInput.checked && !engravedInput.disabled) {
        engravedInput.click();
      }
    }

    // Blocks add to cart when the engraved variant is selected but no
    // engraving text was confirmed, so the fee can never be charged
    // without the engraving details on the order.
    #guardSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form !== this.productForm) return;

      const engravedInput = this.#picker?.querySelector(
        '.engraving-variant .variant-picker__values label:last-child input'
      );
      if (!engravedInput?.checked) return;

      const engravingValue = form.querySelector('[name="properties[Engraving]"]')?.value;
      if (engravingValue) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showError(this.dataset.errorMissing);
      this.layerElement?.open?.();
    };

    #showError(message) {
      if (!this.refs.error || !message) return;
      this.refs.error.textContent = message;
      this.refs.error.hidden = false;
    }

    #hideError() {
      if (!this.refs.error) return;
      this.refs.error.hidden = true;
    }

    #changeType() {
      if (!this.refs.type) return;
      const selectedType = this.refs.type.find((type) => type.checked);
      if (selectedType && this.refs.content && this.refs.note) {
        const index = selectedType.dataset.index;
        const maxCharacter = selectedType.dataset.maxCharacter;

        this.refs.content.forEach((content) => {
          content.classList.remove('--show');
        });
        const activeContent = this.refs.content.find((content) => content.dataset.index === index);
        if (activeContent) {
          activeContent.classList.add('--show');
        }

        this.refs.note.querySelector('label').setAttribute('data-max-character', maxCharacter);
        this.refs.note.querySelector('textarea').setAttribute('maxlength', maxCharacter);
        this.refs.note.querySelector('textarea').value = '';
        this.#hideError();
      }
    }
  }

  customElements.define('product-engraving', ProductEngraving);
}
